import { useStore, useDispatch, type Tool } from '../state/store';

const TOOLS: { id: Tool; label: string; hint: string; key: string }[] = [
  { id: 'select', label: 'Select', hint: 'Select, move, box-select. Drag the amber handle to resize a zone.', key: '1' },
  { id: 'point', label: 'Point', hint: 'Click to drop a single spawn position.', key: '2' },
  { id: 'zone', label: 'Zone', hint: 'Drag from the centre outwards to draw a territory circle.', key: '3' },
  { id: 'scatter', label: 'Scatter', hint: 'Paint many spawn positions with the brush.', key: '4' },
  { id: 'erase', label: 'Erase', hint: 'Paint over positions to delete them.', key: '5' },
  { id: 'measure', label: 'Measure', hint: 'Drag to measure a distance in metres.', key: '6' },
];

/** `compact` drops the view toggles (grid, labels, snap) - the mobile menu shows them via {@link ViewOptions}. */
export function Toolbar({ compact = false }: { compact?: boolean }) {
  const state = useStore();
  const dispatch = useDispatch();
  const { settings } = state;

  return (
    <div className={`toolbar ${compact ? 'compact' : ''}`}>
      <div className="tool-group">
        {TOOLS.map((tool) => (
          <button
            key={tool.id}
            type="button"
            className={`tool ${state.tool === tool.id ? 'active' : ''}`}
            title={`${tool.hint} (${tool.key})`}
            onClick={() => dispatch({ type: 'setTool', tool: tool.id })}
          >
            {!compact && <span className="tool-key">{tool.key}</span>}
            {tool.label}
          </button>
        ))}
      </div>

      <div className="tool-group">
        <button type="button" onClick={() => dispatch({ type: 'undo' })} disabled={state.past.length === 0}>
          Undo
        </button>
        <button type="button" onClick={() => dispatch({ type: 'redo' })} disabled={state.future.length === 0}>
          Redo
        </button>
        <button
          type="button"
          onClick={() => dispatch({ type: 'deleteSelection' })}
          disabled={state.selection.length === 0}
        >
          Delete ({state.selection.length})
        </button>
      </div>

      {(state.tool === 'scatter' || state.tool === 'erase') && (
        <div className="tool-group">
          <label>
            Brush
            <input
              type="range"
              min={10}
              max={1500}
              step={10}
              value={settings.brushRadius}
              onChange={(e) => dispatch({ type: 'setSettings', patch: { brushRadius: Number(e.target.value) } })}
            />
            <span className="value">{settings.brushRadius} m</span>
          </label>
          {state.tool === 'scatter' && (
            <label>
              Spacing
              <input
                type="range"
                min={5}
                max={500}
                step={5}
                value={settings.brushSpacing}
                onChange={(e) => dispatch({ type: 'setSettings', patch: { brushSpacing: Number(e.target.value) } })}
              />
              <span className="value">{settings.brushSpacing} m</span>
            </label>
          )}
        </div>
      )}

      {state.tool === 'zone' && (
        <div className="tool-group">
          <label>
            Default radius
            <input
              type="number"
              min={10}
              step={10}
              value={settings.defaultZoneRadius}
              onChange={(e) =>
                dispatch({ type: 'setSettings', patch: { defaultZoneRadius: Number(e.target.value) || 100 } })
              }
            />
          </label>
        </div>
      )}

      {!compact && (
        <div className="tool-group push-right">
          <ViewOptions />
        </div>
      )}
    </div>
  );
}

/** Grid, label and snap toggles. */
export function ViewOptions() {
  const { settings } = useStore();
  const dispatch = useDispatch();
  return (
    <>
      <label className="check">
        <input
          type="checkbox"
          checked={settings.showGrid}
          onChange={(e) => dispatch({ type: 'setSettings', patch: { showGrid: e.target.checked } })}
        />
        Grid
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={settings.showLabels}
          onChange={(e) => dispatch({ type: 'setSettings', patch: { showLabels: e.target.checked } })}
        />
        Labels
      </label>
      <label>
        Snap
        <select
          value={settings.snap}
          onChange={(e) => dispatch({ type: 'setSettings', patch: { snap: Number(e.target.value) } })}
        >
          <option value={0}>off</option>
          <option value={5}>5 m</option>
          <option value={10}>10 m</option>
          <option value={50}>50 m</option>
          <option value={100}>100 m</option>
        </select>
      </label>
    </>
  );
}
